import { Lock } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useMe } from "@/features/members/hooks/useMe";
import { useApplyCta } from "@/features/members/hooks/useApplyCta";
import { clerkEnabled } from "@/services/clerk";
import { REDIRECT_PARAM } from "@/lib/redirects";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/**
 * What a page says when the server will not serve it to this reader.
 *
 * The directory has a preview anybody may read; the archive does not, and the
 * registration form promises it to members. Without this the page renders its
 * own empty state — a loading skeleton that never resolves, or "nothing
 * matches those filters" — which reads as a broken site rather than a closed
 * door. Same card signed out and signed in on the preview, because it is the
 * same answer: the only difference is whether signing in is also offered.
 */
export function MembersOnly({ what }: { what: string }) {
  const { data: me } = useMe(clerkEnabled);
  const { pathname, search } = useLocation();
  const signedIn = Boolean(me?.signedIn);
  const label = useApplyCta();
  return (
    <Card className="animate-page-enter mt-6">
      <CardHeader>
        <Lock className="text-muted-foreground mb-2 size-8" />
        <CardTitle>Members only</CardTitle>
        <CardDescription>
          {what} is part of membership, along with the listserv and its archive.
          Membership is by application, and the directory preview is open to
          everyone in the meantime.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Button variant="brand" asChild>
          <Link to="/apply">{label}</Link>
        </Button>
        {!signedIn && (
          <Button variant="outline" asChild>
            <Link
              to={`/sign-in?${REDIRECT_PARAM}=${encodeURIComponent(pathname + search)}`}
            >
              Sign in
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default MembersOnly;
